import React, { useState } from 'react';
import { Edit2, Trash2, Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Category } from '../hooks/useCategories';
import { Transaction } from '../mocks/mockData';
import { ExpenseCard } from './ExpenseCard';

interface CategoryListProps {
  categories: Category[];
  onUpdate?: (id: string, updates: Partial<Category>) => void;
  onDelete?: (id: string) => void;
  totalBudget?: number;
  transactions?: Transaction[];
  readOnly?: boolean;
}

export function CategoryList({ categories, onUpdate, onDelete, totalBudget, transactions = [], readOnly = false }: CategoryListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    name: string;
    targetPercentage: string;
    limitAmount: string;
  }>({ name: '', targetPercentage: '', limitAmount: '' });
  const [error, setError] = useState<string | null>(null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<string>>(new Set());

  const toggleExpand = (categoryId: string) => {
    setExpandedCategoryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const startEditing = (category: Category) => {
    setEditingId(category.id);
    setEditForm({
      name: category.name,
      targetPercentage: category.targetPercentage?.toString() || '',
      limitAmount: category.limitAmount?.toString() || ''
    });
    setError(null);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({ name: '', targetPercentage: '', limitAmount: '' });
    setError(null);
  };

  const saveEdit = () => {
    if (!editingId || !onUpdate) return;
    
    try {
      onUpdate(editingId, {
        name: editForm.name,
        targetPercentage: editForm.targetPercentage ? parseFloat(editForm.targetPercentage) : undefined,
        limitAmount: editForm.limitAmount ? parseFloat(editForm.limitAmount) : undefined
      });
      setEditingId(null);
      setEditForm({ name: '', targetPercentage: '', limitAmount: '' });
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleDelete = (category: Category) => {
    if (category.isFixed || !onDelete) return;
    
    if (window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
      try {
        onDelete(category.id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete category');
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (categories.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500 dark:text-gray-400">No categories found. Add your first category to get started!</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}
      
      {categories.map((category) => {
        const calculatedAmount = category.targetPercentage && totalBudget 
          ? (category.targetPercentage / 100) * totalBudget 
          : null;
        
        const isExpanded = expandedCategoryIds.has(category.id);
        const categoryTransactions = transactions.filter(t => t.category === category.name);

        return (
          <div
            key={category.id}
            className="flex flex-col bg-gray-50 dark:bg-gray-700/50 rounded-lg border dark:border-gray-600 overflow-hidden"
          >
            <div className="flex flex-col p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3 flex-1">
                  <button
                    onClick={() => toggleExpand(category.id)}
                    className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>

                  {/* Color indicator */}
                  <div
                    className="w-4 h-4 rounded-full border-2 border-white dark:border-gray-600 shadow-sm"
                    style={{ backgroundColor: category.color }}
                  />
                  
                  {/* Category name or input */}
                  {editingId === category.id ? (
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      onKeyDown={handleKeyPress}
                      className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-500 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                      placeholder="Category Name"
                    />
                  ) : (
                    <div className="flex-1 cursor-pointer" onClick={() => toggleExpand(category.id)}>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">{category.name}</h4>
                      {category.isFixed && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">Fixed category</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!readOnly && (
                  <div className="flex items-center space-x-2 ml-4">
                    {editingId === category.id ? (
                      <>
                        <button
                          onClick={saveEdit}
                          className="p-1 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 rounded"
                          title="Save"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 rounded"
                          title="Cancel"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditing(category)}
                          className="p-1 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                          title="Edit category"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          disabled={category.isFixed}
                          className={`p-1 rounded ${
                            category.isFixed
                              ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                              : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30'
                          }`}
                          title={category.isFixed ? 'Cannot delete fixed categories' : 'Delete category'}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Budget Details */}
              <div className="pl-11 grid grid-cols-2 gap-4 text-sm">
                {editingId === category.id ? (
                  <>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Target %</label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={editForm.targetPercentage}
                          onChange={(e) => setEditForm({ ...editForm, targetPercentage: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-500 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 pr-6"
                          placeholder="0"
                        />
                        <span className="absolute right-2 top-1 text-gray-500 dark:text-gray-400">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Limit Amount</label>
                      <div className="relative">
                        <span className="absolute left-2 top-1 text-gray-500 dark:text-gray-400">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editForm.limitAmount}
                          onChange={(e) => setEditForm({ ...editForm, limitAmount: e.target.value })}
                          className="w-full pl-5 pr-2 py-1 border border-gray-300 dark:border-gray-500 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-gray-600 dark:text-gray-300">
                      <span className="font-medium">Target:</span>{' '}
                      {category.targetPercentage ? `${category.targetPercentage}%` : 'None'}
                      {calculatedAmount !== null && (
                        <span className="text-gray-400 dark:text-gray-500 ml-1">
                          ({formatCurrency(calculatedAmount)})
                        </span>
                      )}
                    </div>
                    <div className="text-gray-600 dark:text-gray-300">
                      <span className="font-medium">Limit:</span>{' '}
                      {category.limitAmount ? formatCurrency(category.limitAmount) : 'None'}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Expanded Transactions List */}
            {isExpanded && (
              <div className="border-t dark:border-gray-600 bg-white dark:bg-gray-800">
                {categoryTransactions.length > 0 ? (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {categoryTransactions.map(transaction => (
                      <div key={transaction.id} className="p-3 pl-12 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{transaction.description}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {transaction.merchant} • {new Date(transaction.date).toLocaleDateString()}
                            </p>
                          </div>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(transaction.amount)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 pl-12 text-sm text-gray-500 dark:text-gray-400 italic">
                    No transactions found for this category.
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}